import React, { useMemo, useState } from "react";
import styled from "styled-components";
import KPICard from "./components/KPICard";
import ReasonBadge from "./components/ReasonBadge";
import { useRejectionStats } from "./hooks/useRejectionStats";

interface RejectionsTabProps {
  node: "tamrur" | "arabic";
}

type SortKey =
  | "source_link"
  | "reason"
  | "stage"
  | "count"
  | "last_seen_at";

const RejectionsTab: React.FC<RejectionsTabProps> = ({ node }) => {
  const { data, loading, error } = useRejectionStats(node);

  const [search, setSearch] = useState("");
  const [disposition, setDisposition] = useState("");
  const [stage, setStage] = useState("");

  const [sortKey, setSortKey] = useState<SortKey>("count");
  const [sortAsc, setSortAsc] = useState(false);

  const filtered = useMemo(() => {
    if (!data) return [];

    const q = search.toLowerCase();

    return data.rows.filter((row) => {
      const matchesSearch =
        !q || (row.source_link ?? "").toLowerCase().includes(q);

      const matchesDisposition =
        !disposition || row.disposition === disposition;

      const matchesStage =
        !stage || row.stage === stage;

      return matchesSearch && matchesDisposition && matchesStage;
    });
  }, [data, search, disposition, stage]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";

      if (av < bv) return sortAsc ? -1 : 1;
      if (av > bv) return sortAsc ? 1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortAsc]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc((a) => !a);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const sortIndicator = (key: SortKey) =>
    sortKey === key ? (sortAsc ? " ▲" : " ▼") : "";

  return (
    <Container>
      {data && (
        <>
          <KPICardsAndFiltersContainer>
            <KPICards>
              <KPICard label="Total Rejected" value={data.totals.all} />

              <KPICard
                label="Normal"
                value={data.totals.normal}
                accentHealthy
                />

              <KPICard
                label="Source-Blocked"
                value={data.totals.source}
                accent
                />

              <KPICard
                label="Our Issue"
                value={data.totals.ours}
                accentSilent
                />
            </KPICards>

            <Filters>
              <SearchInput
                type="text"
                placeholder="Search source..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                />

              <Select
                value={disposition}
                onChange={(e) => setDisposition(e.target.value)}
                >
                <option value="">All Dispositions</option>
                <option value="normal">Normal</option>
                <option value="source">Source</option>
                <option value="ours">Ours</option>
              </Select>

              <Select
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                >
                <option value="">All Stages</option>
                <option value="discovered">discovered</option>
                <option value="screened">screened</option>
                <option value="fetched">fetched</option>
                <option value="extracted">extracted</option>
                <option value="filtered">filtered</option>
                <option value="stored">stored</option>
              </Select>
            </Filters>
          </KPICardsAndFiltersContainer>

          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th onClick={() => toggleSort("source_link")}>
                    Source{sortIndicator("source_link")}
                  </Th>

                  <Th onClick={() => toggleSort("reason")}>
                    Reason{sortIndicator("reason")}
                  </Th>

                  <Th onClick={() => toggleSort("stage")}>
                    Stage{sortIndicator("stage")}
                  </Th>

                  <Th align="right" onClick={() => toggleSort("count")}>
                    Count{sortIndicator("count")}
                  </Th>

                  <Th
                    align="right"
                    onClick={() => toggleSort("last_seen_at")}
                  >
                    Last Seen{sortIndicator("last_seen_at")}
                  </Th>
                </tr>
              </thead>

              <tbody>
                {sorted.length === 0 ? (
                  <tr>
                    <Td colSpan={5}>
                      <Msg>No rejections found.</Msg>
                    </Td>
                  </tr>
                ) : (
                  sorted.map((row, i) => (
                    <Tr
                      key={`${row.source_link ?? "unknown"}-${row.reason}-${i}`}
                    >
                      <Td>
                        <SourceUrl>{row.source_link ?? "-"}</SourceUrl>
                      </Td>

                      <Td>
                        <ReasonBadge
                          reason={row.reason}
                          disposition={row.disposition}
                          label={row.reason_label}
                        />
                      </Td>

                      <Td>{row.stage}</Td>

                      <Td align="right">
                        {row.count.toLocaleString()}
                      </Td>

                      <Td align="right">
                        {row.last_seen_at
                          ? new Date(row.last_seen_at).toLocaleString()
                          : "-"}
                      </Td>
                    </Tr>
                  ))
                )}
              </tbody>
            </Table>
          </TableWrapper>
          <CardList>
            {sorted.length === 0 ? (
              <Msg>No rejections found.</Msg>
            ) : (
              sorted.map((row, i) => (
                <Card
                  key={`${row.source_link ?? "unknown"}-${row.reason}-${i}`}
                >
                  <CardHeader>
                    <SourceUrl>{row.source_link ?? "-"}</SourceUrl>
                    <ReasonBadge
                      reason={row.reason}
                      disposition={row.disposition}
                      label={row.reason_label}
                    />
                  </CardHeader>

                  <CardGrid>
                    <CardCell>
                      <CellLabel>Stage</CellLabel>
                      <CellValue>{row.stage}</CellValue>
                    </CardCell>

                    <CardCell>
                      <CellLabel>Count</CellLabel>
                      <CellValue>{row.count.toLocaleString()}</CellValue>
                    </CardCell>

                    <CardCell>
                      <CellLabel>Last Seen</CellLabel>
                      <CellValue>
                        {row.last_seen_at
                          ? new Date(row.last_seen_at).toLocaleString()
                          : "-"}
                      </CellValue>
                    </CardCell>
                  </CardGrid>
                </Card>
              ))
            )}
          </CardList>
        </>
      )}

      {loading && <Msg>Loading…</Msg>}
      {error && <ErrorMsg>{error}</ErrorMsg>}
    </Container>
  );
};

export default RejectionsTab;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const KPICardsAndFiltersContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 14px;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;

  @media (max-width: 480px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    width: 100%;
  }
`;

const KPICards = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    width: 100%;
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  max-height: 40px;
`;

const SearchInput = styled.input`
  padding: 8px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  min-width: 260px;
  &:focus { outline: none; border-color: #0058A9; }

  @media (max-width: 480px) {
    width: 100%;
    min-width: 0;
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  font-size: 13px;
`;

const Msg = styled.p`
  color: #aaa;
  font-size: 14px;
  padding: 8px 0;
`;

const ErrorMsg = styled.p`
  color: #c62828;
  font-size: 14px;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);

  @media (max-width: 768px) {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: #fff;
  min-width: 750px;
`;

const CardList = styled.div`
  display: none;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);
  padding: 14px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #f6f9ff; }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 10px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 14px;
`;

const CardCell = styled.div`
  display: flex;
  flex-direction: column;
`;

const CellLabel = styled.span`
  font-size: 10px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const CellValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #222;
  word-break: break-word;
`;

const Th = styled.th<{ align?: string }>`
  background: #0058A9;
  color: #fff;
  text-align: ${({ align }) => align ?? "left"};
  padding: 10px 14px;
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #004a90;
  }
`;

const Tr = styled.tr`
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;

  &:hover {
    background: #f6f9ff;
  }
`;

const Td = styled.td<{ align?: string }>`
  padding: 9px 14px;
  text-align: ${({ align }) => align ?? "left"};
  vertical-align: middle;
`;

const SourceUrl = styled.span`
  font-size: 12px;
  color: #0058A9;
  word-break: break-all;
`;